import { styled } from '@linaria/react';
import { motion } from 'framer-motion';

import ClientOnlySnippet from '@/components/CodeSnippet/ClientOnlySnippet';
import { DemoToggleButtonGroupControl } from '@/components/Demo';

import GridDemo from './GridDemo';
import Item from './Item';
import { GridStructure, BaseCodeWrapper } from './Grid.shared';

function PercentageVsFraction() {
  return (
    <GridDemo
      hidePerspectiveControl
      defaultPerspectiveEnabled={false}
      initialValues={{
        unit: 'fr',
        width: 50,
      }}
      customGrid={{
        gridTemplateColumns: '1fr',
      }}
      controls={[
        <ControlWrapper key="unit">
          <DemoToggleButtonGroupControl
            id="unit"
            label="Unit"
            options={[
              { label: 'Fractions', value: 'fr' },
              { label: 'Percentages', value: '%' },
            ]}
          />
        </ControlWrapper>,
      ]}
    >
      {({ unit, isPerspectiveEnabled }) => {
        const cssCode = `\
.parent {
  display: grid;
  grid-template-columns: ${unit === 'fr' ? '1fr 3fr' : '25% 75%'};
  gap: 16px;
}`;
        return (
          <Row>
            <CodeWrapper>
              <ClientOnlySnippet
                omitCopyButton
                lockedToColorMode="dark"
                code={cssCode}
                lang="css"
              />
            </CodeWrapper>
            <Wrapper
              style={{
                '--width': 250 + 'px',
              }}
            >
              <Layer
                style={{
                  transform: isPerspectiveEnabled
                    ? 'translateX(10%) skewY(18deg) scaleX(0.85)'
                    : 'translateX(0%) skewY(0deg) scaleX(1)',
                }}
              >
                <GridStructure
                  style={{
                    gridTemplateColumns:
                      unit === '%' ? '25% 16px 75%' : '1fr 16px 3fr',
                  }}
                >
                  <GridBackground />
                  <GridColumn />
                </GridStructure>
              </Layer>
              <Layer
                style={{
                  transform: isPerspectiveEnabled
                    ? 'translateX(-10%) skewY(18deg) scaleX(0.85)'
                    : 'translateX(0%) skewY(0deg) scaleX(1)',
                }}
              >
                <ChildWrapper
                  style={{
                    gridTemplateColumns:
                      unit === '%' ? '25% 75%' : '1fr 3fr',
                    gap: 16,
                  }}
                >
                  <Item layout={false} />
                  <Item layout={false}>
                    {unit === '%' && <OverflowIndicator />}
                  </Item>
                </ChildWrapper>
              </Layer>
            </Wrapper>
          </Row>
        );
      }}
    </GridDemo>
  );
}

const ControlWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 32px;
  margin: 32px 0px 16px;

  @media (max-width: 46.875rem) {
    flex-direction: column-reverse;
  }

  @media (max-width: 25.625rem) {
    margin-bottom: -16px;
  }
`;

const Wrapper = styled.div`
  position: relative;
  width: var(--width);
  max-width: calc(100vw - 32px);
  height: 190px;
`;

const Layer = styled(motion.div)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  transition: transform 500ms;
  will-change: transform;
`;

const ChildWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
`;

const GridBackground = styled.div`
  grid-row: 1 / 3;
  grid-column: 2;
  width: 18px;
  background: repeating-linear-gradient(
    45deg,
    hsl(210deg 40% 80% / 0.06) 0px,
    hsl(210deg 40% 80% / 0.06) 10px,
    hsl(210deg 40% 80% / 0) 10px,
    hsl(210deg 40% 80% / 0) 20px
  );
  transform: translateX(-2px);
`;
const GridColumn = styled.div`
  grid-row: 1 / 3;
  grid-column: 2;
  border-right: 2px dashed var(--color-gray-500);
  width: 0px;
  transform: translateX(5.5px);
`;

const CodeWrapper = styled(BaseCodeWrapper)`
  width: 100%;
  min-width: 18.75rem;
  max-width: 25.625rem;
  min-height: 11.875rem;

  /* Fill the full space on mobile */
  @media (max-width: 25.625rem) {
    && {
      width: calc(100% + var(--viewport-padding) * 2);
      max-width: calc(100% + var(--viewport-padding) * 2);
      margin-inline: calc(var(--viewport-padding) * -1);
      padding-inline: 4px;
      border-radius: 0px 0px 5px 5px;
    }
  }
`;

const OverflowIndicator = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 18px;
  height: 100%;
  background: hsl(350deg 100% 50% / 0.4);
  border: 2px solid hsl(350deg 100% 50% / 0.7);
  border-left: 0px;
  border-radius: 0px 4px 4px 0px;
`;

export default PercentageVsFraction;
